import React, {useEffect, useRef} from 'react';
import {Animated, StyleSheet, View} from 'react-native';
import {lbColors, lbRadius} from '../leaderboardTheme';

/**
 * Pulsing placeholders for the leaderboard while data loads, so the card/list
 * reserves its space and fills in — instead of popping in after the rest of
 * the screen. `compact` = the dashboard preview's shorter rows.
 */
function usePulse() {
  const pulse = useRef(new Animated.Value(0.5)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 700,
          useNativeDriver: false,
        }),
        Animated.timing(pulse, {
          toValue: 0.5,
          duration: 700,
          useNativeDriver: false,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);
  return pulse;
}

export function SkeletonRow({
  compact = false,
  wide = false,
}: {
  compact?: boolean;
  wide?: boolean;
}) {
  const pulse = usePulse();
  const avatar = compact ? 32 : 40;
  return (
    <View style={[styles.row, {height: compact ? 52 : 64}]}>
      <Animated.View style={[styles.rank, {opacity: pulse}]} />
      <Animated.View
        style={[
          styles.avatar,
          {width: avatar, height: avatar, borderRadius: avatar / 2, opacity: pulse},
        ]}
      />
      <View style={styles.mid}>
        <Animated.View style={[styles.nameBar, {opacity: pulse}]} />
        {!compact ? (
          <Animated.View style={[styles.subBar, {opacity: pulse}]} />
        ) : null}
      </View>
      {wide ? (
        <Animated.View style={[styles.ratingBar, {opacity: pulse}]} />
      ) : null}
      <Animated.View style={[styles.winsBar, {opacity: pulse}]} />
    </View>
  );
}

export function SkeletonRows({
  count = 5,
  compact = false,
  wide = false,
}: {
  count?: number;
  compact?: boolean;
  wide?: boolean;
}) {
  return (
    <>
      {Array.from({length: count}).map((_, i) => (
        <SkeletonRow key={i} compact={compact} wide={wide} />
      ))}
    </>
  );
}

export function SkeletonChip() {
  const pulse = usePulse();
  return (
    <View style={styles.chip}>
      <Animated.View style={[styles.chipLabel, {opacity: pulse}]} />
      <View style={styles.chipBody}>
        <Animated.View
          style={[styles.avatar, {width: 52, height: 52, borderRadius: 26, opacity: pulse}]}
        />
        <View style={{flex: 1}}>
          <Animated.View style={[styles.chipBig, {opacity: pulse}]} />
          <Animated.View style={[styles.subBar, {marginTop: 6, opacity: pulse}]} />
        </View>
      </View>
    </View>
  );
}

const bar = {backgroundColor: lbColors.line, borderRadius: 6} as const;

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 12,
  },
  rank: {width: 18, height: 14, ...bar},
  avatar: {backgroundColor: lbColors.line},
  mid: {flex: 1, gap: 6},
  nameBar: {width: '55%', height: 13, ...bar},
  subBar: {width: '35%', height: 10, ...bar},
  ratingBar: {width: 40, height: 13, ...bar},
  winsBar: {width: 26, height: 18, ...bar},
  chip: {
    backgroundColor: lbColors.youChipBg,
    borderRadius: lbRadius.lg + 2,
    padding: 22,
    justifyContent: 'center',
  },
  chipLabel: {width: 130, height: 11, ...bar, marginBottom: 14},
  chipBody: {flexDirection: 'row', alignItems: 'center', gap: 14},
  chipBig: {width: 64, height: 24, ...bar},
});
